/**
 * Prompt Validation Service
 * 
 * Validates prompts for unsafe patterns, injection attempts, and medical safety.
 */

// Unsafe patterns that could indicate prompt injection
const UNSAFE_PATTERNS = [
  // Prompt injection attempts
  /(?:ignore|forget|disregard)\s+(?:previous|above|all)\s+(?:instructions|prompts?|rules?)/i,
  /(?:new|different)\s+(?:instructions?|prompts?|rules?|system)/i,
  /(?:you\s+are|act\s+as|pretend\s+to\s+be)\s+(?:a\s+)?(?:different|new)/i,
  /(?:system|assistant|user):\s*(?:ignore|override)/i,
  
  // Code injection attempts
  /<script[^>]*>[\s\S]*?<\/script>/gi,
  /javascript:/gi,
  /on\w+\s*=\s*["'][^"']*["']/gi,
  
  // SQL injection patterns
  /(?:union|select|insert|update|delete|drop|exec|execute)\s+.*from/i,
  /['";]\s*(?:or|and)\s+['"]?\d+['"]?\s*=\s*['"]?\d+/i,
  
  // Command injection (only flag in suspicious command-like contexts, not in JSON examples)
  // Removed overly broad pattern that was flagging JSON examples in prompts
  // /[;&|`$(){}[\]]/g, // This was too broad - JSON examples contain {}[]
  
  // Attempts to bypass safety
  /(?:bypass|circumvent|override)\s+(?:safety|security|filter|validation)/i,
];

// Medical safety patterns
const MEDICAL_SAFETY_PATTERNS = [
  // Dangerous instructions
  /(?:prescribe|recommend|suggest)\s+(?:without|ignoring)\s+(?:contraindications|allergies|interactions)/i,
  /(?:always|never)\s+(?:prescribe|recommend|treat)/i,
  /(?:guarantee|promise)\s+(?:cure|treatment|outcome)/i,
];

/**
 * Validate a prompt for unsafe patterns
 * @param {string} prompt - Prompt text to validate
 * @returns {Object} Validation results
 */
export const validatePrompt = (prompt) => {
  const issues = [];
  const warnings = [];
  
  // Check for unsafe patterns
  UNSAFE_PATTERNS.forEach((pattern, index) => {
    if (pattern.test(prompt)) {
      issues.push({
        type: 'unsafe_pattern',
        severity: 'error',
        pattern: pattern.toString(),
        message: 'Potential prompt injection or unsafe pattern detected',
      });
    }
  });
  
  // Check for medical safety issues
  MEDICAL_SAFETY_PATTERNS.forEach((pattern, index) => {
    if (pattern.test(prompt)) {
      warnings.push({
        type: 'medical_safety',
        severity: 'warning',
        pattern: pattern.toString(),
        message: 'Potentially unsafe medical instruction detected',
      });
    }
  });
  
  // Check prompt length (extremely long prompts might be suspicious)
  if (prompt.length > 50000) {
    warnings.push({
      type: 'excessive_length',
      severity: 'warning',
      message: 'Prompt is extremely long. Consider breaking into smaller sections.',
    });
  }
  
  return {
    isValid: issues.length === 0,
    hasWarnings: warnings.length > 0,
    issues,
    warnings,
  };
};

/**
 * Sanitize prompt (remove or escape unsafe patterns)
 * @param {string} prompt - Prompt to sanitize
 * @returns {string} Sanitized prompt
 */
export const sanitizePrompt = (prompt) => {
  let sanitized = prompt;
  
  // Remove script tags
  sanitized = sanitized.replace(/<script[^>]*>[\s\S]*?<\/script>/gi, '[REMOVED: Script tag]');
  
  // Escape potentially dangerous characters in suspicious contexts
  // (This is a simplified version - more sophisticated sanitization may be needed)
  
  return sanitized;
};

/**
 * Add medical disclaimer to prompt
 * @param {string} prompt - Original prompt
 * @returns {string} Prompt with medical disclaimer
 */
export const addMedicalDisclaimer = (prompt) => {
  const disclaimer = `\n\n=== IMPORTANT MEDICAL DISCLAIMER ===
This AI-generated content is for informational purposes only and should not be used as a substitute for professional medical advice, diagnosis, or treatment. Always seek the advice of qualified health providers with any questions regarding a medical condition. Never disregard professional medical advice or delay seeking it because of information provided here.
=== END DISCLAIMER ===\n\n`;
  
  // Insert disclaimer before the main prompt content
  return disclaimer + prompt;
};

const promptValidationService = {
  validatePrompt,
  sanitizePrompt,
  addMedicalDisclaimer,
};

export default promptValidationService;
